import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import WarningIcon from '@mui/icons-material/Warning';
import { styled } from '@mui/material/styles';
import notify from '../../../../../../../shared/feedback/notifications';
import { Translate } from '../../../../../../../shared/LanguageService';
import { AuthenticatedUser } from '../../../../../../../shared/types';
import { useUploadImagesMutation } from '../queries/use-mutation-upload-images';
import {
    ACCEPTED_IMAGE_TYPES,
    FileRejection,
    OrderRowFileName,
    UploadVariant,
    validateFile
} from '../state/utils';

const DropArea = styled('div')<{ isDragging: boolean }>(({ isDragging }) => ({
    position: 'relative',
    minHeight: '200px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    padding: '16px',
    border: `6px dashed ${isDragging ? '#7dab9b' : '#e1e1e1'}`,
    borderRadius: '6px',
    backgroundColor: '#fff',
    cursor: 'pointer'
}));

const Previews = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '24px 32px',
    padding: '12px 8px'
});

const Preview = styled('figure')({
    position: 'relative',
    margin: 0,
    width: '160px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
});

const PreviewImage = styled('img', {
    shouldForwardProp: (prop) => prop !== 'hasWarning'
})<{ hasWarning: boolean }>(({ hasWarning }) => ({
    width: '160px',
    height: '110px',
    objectFit: 'cover',
    borderRadius: '4px',
    backgroundColor: '#ededed',
    boxSizing: 'border-box',
    border: hasWarning ? '2px solid #f0ad4e' : '2px solid transparent'
}));

const WarningBadge = styled('span')({
    position: 'absolute',
    top: '-10px',
    left: '-10px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '14px',
    backgroundColor: '#f0ad4e',
    color: '#000',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)'
});

const PreviewName = styled('figcaption')({
    fontSize: '13px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
});

const RemoveButton = styled(IconButton)({
    position: 'absolute',
    top: '-10px',
    right: '-10px',
    width: '28px',
    height: '28px',
    padding: 0,
    color: '#333',
    backgroundColor: '#fff',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
    transition: 'background-color 0.15s ease, color 0.15s ease, transform 0.15s ease',
    '&:hover': {
        backgroundColor: '#d93934',
        color: '#fff',
        transform: 'scale(1.1)'
    }
});

const UploadedMark = styled(CheckCircleIcon)({
    position: 'absolute',
    top: '37px',
    left: '62px',
    fontSize: '36px',
    color: '#fff',
    filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5))'
});

const ProgressOverlay = styled('div')({
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '0 10%',
    backgroundColor: 'rgba(245, 245, 245, 0.85)',
    zIndex: 1
});

type QueuedFile = {
    file: File;
    previewUrl: string;
    isUnknownOnOrder: boolean;
};

type ImageDropzoneProps = {
    orderId: number;
    orderRowId: number;
    orderRowFileNames: OrderRowFileName[];
    variant: UploadVariant;
    warnAboutUnknownFilenames: boolean;
    user: AuthenticatedUser;
    onUploaded?: (uploadedFilenames: string[]) => void;
};

function createPreviewUrl(file: File): string {
    return typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : '';
}

function releasePreviewUrl(queued: QueuedFile) {
    if (queued.previewUrl && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(queued.previewUrl);
    }
}

export function ImageDropzone({
    orderId,
    orderRowId,
    orderRowFileNames,
    variant,
    warnAboutUnknownFilenames,
    user,
    onUploaded
}: ImageDropzoneProps) {
    let [queuedFiles, setQueuedFiles] = React.useState<QueuedFile[]>([]);
    let [isDragging, setIsDragging] = React.useState(false);
    let [completedCount, setCompletedCount] = React.useState(0);
    let fileInput = React.useRef<HTMLInputElement>(null);

    let upload = useUploadImagesMutation({
        orderId,
        orderRowId,
        variant,
        user,
        onFileUploaded: setCompletedCount,
        onUploaded
    });

    function addFiles(files: FileList | File[]) {
        let accepted: QueuedFile[] = [];

        Array.from(files).forEach((file) => {
            let validation = validateFile(file, orderRowFileNames);

            if (validation.rejection === FileRejection.InvalidType) {
                notify.failure(`${file.name} ${Translate('dropzone/invalid-filetype')}`);
                return;
            }

            if (validation.rejection === FileRejection.ExtensionMismatch) {
                notify.failure(
                    `${file.name} ${Translate('dropzone/valid-file-unvalid-file-extension')}`
                );
                return;
            }

            let isUnknownOnOrder = validation.isUnknownOnOrder && warnAboutUnknownFilenames;

            if (isUnknownOnOrder) {
                notify.warning(
                    `${file.name} ${Translate('image-processing/notification/does-not-exist-on-order')}`
                );
            }

            accepted.push({ file, previewUrl: createPreviewUrl(file), isUnknownOnOrder });
        });

        setQueuedFiles((current) => current.concat(accepted));
    }

    function removeFile(queued: QueuedFile) {
        releasePreviewUrl(queued);
        setQueuedFiles((current) => current.filter((candidate) => candidate !== queued));
    }

    function clearQueue() {
        queuedFiles.forEach(releasePreviewUrl);
        setQueuedFiles([]);
        setCompletedCount(0);
    }

    function handleDrop(event: React.DragEvent) {
        event.preventDefault();
        setIsDragging(false);
        if (!upload.isLoading) {
            addFiles(event.dataTransfer.files);
        }
    }

    function handleDragOver(event: React.DragEvent) {
        event.preventDefault();
        setIsDragging(true);
    }

    function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
        if (event.target.files) {
            addFiles(event.target.files);
        }
        event.target.value = '';
    }

    function handleUpload() {
        setCompletedCount(0);
        upload.mutateAsync(queuedFiles.map((queued) => queued.file)).then(clearQueue);
    }

    let totalBytes = queuedFiles.reduce((sum, queued) => sum + queued.file.size, 0);
    let completedBytes = queuedFiles
        .slice(0, completedCount)
        .reduce((sum, queued) => sum + queued.file.size, 0);
    let progress = totalBytes === 0 ? 0 : Math.round((completedBytes / totalBytes) * 100);
    let inputId = `${variant}-images-input-${orderRowId}`;
    let uploadLabel = Translate('image-processing/edited/button-labels/upload-images');
    let unknownFileWarning = Translate(
        'image-processing/edited-images/dropzone/does-not-exist-on-order/warning/title'
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <DropArea
                isDragging={isDragging}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={() => setIsDragging(false)}
                onClick={() => !upload.isLoading && fileInput.current && fileInput.current.click()}
                data-testid={`${variant}-images-dropzone`}
            >
                {upload.isLoading && (
                    <ProgressOverlay aria-live="polite">
                        <Typography variant="body1" sx={{ fontSize: '14px' }}>
                            {`${completedCount} / ${queuedFiles.length}`}
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={progress}
                            aria-label={uploadLabel}
                            sx={{ width: '100%', height: '20px', borderRadius: '4px' }}
                        />
                        <Typography variant="body2" sx={{ fontSize: '12px' }}>
                            {`${progress}%`}
                        </Typography>
                    </ProgressOverlay>
                )}
                {queuedFiles.length === 0 && (
                    <Typography variant="body1" sx={{ fontSize: '24px', color: '#c4c4c4' }}>
                        {Translate('dropzone/title')}
                    </Typography>
                )}
                {queuedFiles.length > 0 && (
                    <Previews onClick={(event) => event.stopPropagation()}>
                        {queuedFiles.map((queued, index) => (
                            <Preview
                                key={`${queued.file.name}-${queued.file.size}`}
                                title={queued.isUnknownOnOrder ? unknownFileWarning : undefined}
                            >
                                <PreviewImage
                                    src={queued.previewUrl}
                                    alt=""
                                    hasWarning={queued.isUnknownOnOrder}
                                />
                                {queued.isUnknownOnOrder && (
                                    <WarningBadge
                                        role="img"
                                        aria-label={unknownFileWarning}
                                        data-testid="preview-warning"
                                    >
                                        <WarningIcon sx={{ fontSize: 18 }} />
                                    </WarningBadge>
                                )}
                                <PreviewName title={queued.file.name}>{queued.file.name}</PreviewName>
                                {upload.isLoading && index < completedCount && <UploadedMark />}
                                {!upload.isLoading && (
                                    <RemoveButton
                                        size="small"
                                        aria-label={`${Translate('dropzone/remove-image')} ${
                                            queued.file.name
                                        }`}
                                        onClick={() => removeFile(queued)}
                                    >
                                        <CloseIcon sx={{ fontSize: 16 }} />
                                    </RemoveButton>
                                )}
                            </Preview>
                        ))}
                    </Previews>
                )}
                <input
                    ref={fileInput}
                    id={inputId}
                    type="file"
                    multiple
                    accept={ACCEPTED_IMAGE_TYPES.join(',')}
                    style={{ display: 'none' }}
                    aria-label={Translate('dropzone/title')}
                    onChange={handleInputChange}
                />
            </DropArea>
            <Box>
                <Button
                    variant="contained"
                    startIcon={<CloudUploadIcon />}
                    disabled={upload.isLoading || queuedFiles.length === 0}
                    aria-busy={upload.isLoading}
                    onClick={handleUpload}
                >
                    {uploadLabel}
                </Button>
            </Box>
        </Box>
    );
}
